import { LocalStorageService } from "src/services";
import { dateTimeRange, defaultStartDateRangeDate } from "src/helpers/dateHelper";
import { Filter, DashSettings } from "src/types";
import {
  CheckInsGroupsOptions,
  ColumnHeader,
  CreationDateSortOptions,
  LocalStorageKeys,
  TimestampSortOptions
} from "src/enums";

class DashboardService<
  H extends ColumnHeader,
  F extends Filter,
  S extends CreationDateSortOptions | TimestampSortOptions,
  G extends CheckInsGroupsOptions | undefined
> {
  private readonly localStorageService: LocalStorageService;
  private readonly localStorageDashSettingsKey: LocalStorageKeys;

  constructor(
    localStorageService: LocalStorageService,
    localStorageDashSettingsKey:
      | LocalStorageKeys.PURCHASE_ORDER_DASH_SETTINGS
      | LocalStorageKeys.TRANSIT_PURCHASE_ORDER_DASH_SETTINGS
      | LocalStorageKeys.WAREHOUSE_CHECK_IN_DASH_SETTINGS
      | LocalStorageKeys.USER_ACCOUNTS_DASH_SETTINGS
      | LocalStorageKeys.VENDOR_ACCOUNTS_DASH_SETTINGS
      | LocalStorageKeys.CLAIM_DASH_SETTINGS
      | LocalStorageKeys.TRANSFER_ORDER_DASH_SETTINGS
      | LocalStorageKeys.ACTIVE_ANTS_PURCHASE_ORDER_DASH_SETTINGS
  ) {
    this.localStorageService = localStorageService;
    this.localStorageDashSettingsKey = localStorageDashSettingsKey;
  }

  getLocalStorageDashSettings(): DashSettings<H, F, S, G> | undefined {
    try {
      const dashboardSettings = this.localStorageService.getLocalStorageData<DashSettings<H, F, S, G>>(
        this.localStorageDashSettingsKey
      );
      if (dashboardSettings) {
        dashboardSettings.dateTimeRange = dateTimeRange(
          dashboardSettings?.dateTimeRange?.start?.toString(),
          dashboardSettings?.dateTimeRange?.end?.toString()
        );
        dashboardSettings.deliveryDateTimeRange = dateTimeRange(
          dashboardSettings?.deliveryDateTimeRange?.start?.toString(),
          dashboardSettings?.deliveryDateTimeRange?.end?.toString()
        );
        dashboardSettings.receiptDateTimeRange = dateTimeRange(
          dashboardSettings?.receiptDateTimeRange?.start?.toString(),
          dashboardSettings?.receiptDateTimeRange?.end?.toString()
        );

        if (dashboardSettings.dateTimeRange.start) {
          const threeMonthsAgo = new Date();
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

          if (dashboardSettings.dateTimeRange.start < threeMonthsAgo) {
            dashboardSettings.dateTimeRange.start = defaultStartDateRangeDate();
          }
        }

        if (
          dashboardSettings.dateTimeRange.start &&
          dashboardSettings.dateTimeRange.end &&
          dashboardSettings.dateTimeRange.end < dashboardSettings.dateTimeRange.start
        ) {
          dashboardSettings.dateTimeRange = {
            start: defaultStartDateRangeDate(),
            end: undefined
          };
        }

        return dashboardSettings;
      }
    } catch (error) {
      console.warn("Failed to get local storage dash settings");
    }
  }

  setLocalStorageDashSettings(dashSettings: DashSettings<H, F, S, G>): void {
    try {
      this.localStorageService.setLocalStorageData<DashSettings<H, F, S, G>>(
        this.localStorageDashSettingsKey,
        dashSettings
      );
    } catch (error) {
      console.warn("Failed to set local storage dash settings");
    }
  }
}

export { DashboardService };
